import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { designPath } from '../utils/design';
import { FEATURE_FLAG_NAMES, useFeatureFlag } from './useFeatureFlag';
import { REPORT_TYPES } from '../consts/reportTypes';
import { jurisdictionService } from '../services/jurisdiction.service';

export interface ApproverReportRecord {
  workflow_id: string;
  period_id: string;
  workflow_type?: string;
  is_aifm_included?: boolean;
  status?: unknown[];
}

interface OpenOptions {
  onMissing?: () => void;
  onError?: () => void;
}

/** Opens the report an approver was sent, in the reader's design: Form PF at once, Annex IV once its member state is known. */
export const useOpenApproverReport = () => {
  const navigate = useNavigate();
  const isV2UI = useFeatureFlag(FEATURE_FLAG_NAMES.V2_UI);

  return useCallback(
    async (record: ApproverReportRecord, { onMissing, onError }: OpenOptions = {}) => {
      const reportV2Path = `/report-v2/${record.workflow_id}/${record.period_id}`;
      // Without the flag the new report redirects away, and it has no Custom report at all.
      const pick = (v2: string, classic: string) =>
        isV2UI && record.workflow_type !== REPORT_TYPES.CUSTOM ? designPath(v2, classic) : classic;

      if (record.workflow_type === 'Form PF') {
        navigate(pick(reportV2Path, `/report/pf/us/${record.workflow_id}/${record.period_id}`));
        return;
      }

      try {
        const res: any = await jurisdictionService.getJurisdictionsApprover(record.period_id);
        const jurisdiction = res?.[0];
        if (!jurisdiction) {
          onMissing?.();
          return;
        }

        const reportingMemberState = jurisdiction.reporting_member_state;
        const isFCA = reportingMemberState === 'UK' || reportingMemberState === 'uk';
        const isAifmOnly = record.is_aifm_included && !record.status?.length;
        const groupedQuery = isAifmOnly ? '?type=AIF001' : '';
        const classicPath = isFCA
          ? `/report/fca/uk/${record.workflow_id}/${record.period_id}${groupedQuery}`
          : `/report/esma/${reportingMemberState?.toLowerCase()}/${record.workflow_id}/${record.period_id}${groupedQuery}`;
        const v2Path = `${reportV2Path}?ms=${reportingMemberState}${isAifmOnly ? '&kind=aifm' : ''}`;

        navigate(pick(v2Path, classicPath));
      } catch {
        onError?.();
      }
    },
    [navigate, isV2UI]
  );
};
